import { PlugDisconnectedRegular } from '@fluentui/react-icons';
import { H2, BodyRegular, CaptionRegular } from '@components/atoms/typography';
import Button from '@components/atoms/button';

type ErrorHoldingMessageProps = {
  /** Human name of the surface that failed to load, e.g. "AI Teams". */
  pageTitle: string;
  /** Optional override for the body copy. */
  message?: string;
  /** HTTP status behind the failure, shown as a muted "Error 503" line so it
   * is there for support without shouting at the user. */
  statusCode?: number;
  /** Re-runs the failed request. When omitted, no retry button renders. */
  onRetry?: () => void;
  /** Disables the retry button and shows its spinner while a refetch runs. */
  isRetrying?: boolean;
};

/**
 * Simple, centred error state for paid My Team surfaces whose data fetch has
 * hard failed. Rendered as a top-level return so a failed request can never
 * fall through to a blank screen or a misleading "set up your team" message.
 */
const ErrorHoldingMessage = ({
  pageTitle,
  message,
  statusCode,
  onRetry,
  isRetrying
}: ErrorHoldingMessageProps) => {
  const bodyCopy =
    message ??
    'This is a problem on our end, not your connection. Give it a moment and try again.';

  return (
    <div
      className="flex min-h-[60vh] w-full items-center justify-center px-4 py-16"
      data-cy="error-holding-message"
    >
      <div className="flex max-w-md flex-col items-center text-center">
        <div className="mb-7 rounded-full bg-primary-50/60 p-2.5">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-50">
            <PlugDisconnectedRegular
              className="h-8 w-8 text-primary-600"
              aria-hidden
            />
          </div>
        </div>

        <H2 className="mb-2">We couldn&apos;t load {pageTitle}</H2>

        <BodyRegular className="mb-0 text-neutral-600">{bodyCopy}</BodyRegular>

        {onRetry && (
          <div className="mt-7">
            <Button
              variant="primary"
              onClick={onRetry}
              loading={isRetrying}
              dataCy="error-holding-retry"
            >
              Try again
            </Button>
          </div>
        )}

        {statusCode && (
          <CaptionRegular className="mt-6 text-neutral-400">
            Error {statusCode}
          </CaptionRegular>
        )}
      </div>
    </div>
  );
};

export default ErrorHoldingMessage;
